/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  MexcoreapipbEcomMigrationStatus,
  MexcoreapipbGetEcomMigrationCSVResponse,
} from "generated/mexcoreapi-sellercenter.type"

interface EcomMigrationCSVQueryConstraint {
  isEnabled: boolean
  merchantGroupID?: string
  isEligibleSurface: boolean
}

type EcomMigrationReviewData = Pick<
  MexcoreapipbGetEcomMigrationCSVResponse,
  "migrationStatus" | "reviewEndAt"
>

export const shouldFetchEcomMigrationCSV = ({
  isEnabled,
  merchantGroupID,
  isEligibleSurface,
}: EcomMigrationCSVQueryConstraint): boolean =>
  isEnabled && !!merchantGroupID && isEligibleSurface

// RFC3339Nano carries up to 9 fractional digits while Date parses at
// millisecond precision — truncate the excess so the represented UTC instant
// is stable regardless of engine rounding.
export const parseEcomMigrationTimestamp = (value: string): number | null => {
  if (!value) {
    return null
  }

  const normalized = value.replace(/(\.\d{3})\d+/, "$1")
  const parsed = new Date(normalized).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

export const isPendingEcomMigrationReview = (
  migrationData?: EcomMigrationReviewData,
  nowMs = Date.now(),
): boolean => {
  if (
    migrationData?.migrationStatus !==
    MexcoreapipbEcomMigrationStatus.PENDING_REVIEW
  ) {
    return false
  }

  const reviewEndAtMs = parseEcomMigrationTimestamp(migrationData.reviewEndAt)
  return reviewEndAtMs !== null && reviewEndAtMs > nowMs
}

const MS_PER_DAY = 86_400_000

/** Whole days remaining in the review window. A partial day still counts. */
export const getEcomMigrationReviewDaysLeft = (
  reviewEndAtMs: number | null,
  nowMs: number,
): number | null => {
  if (reviewEndAtMs === null || reviewEndAtMs <= nowMs) {
    return null
  }

  return Math.ceil((reviewEndAtMs - nowMs) / MS_PER_DAY)
}
